// src/components/Console/Staff/pages/ReceptionPayments.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackButton } from '../../../../../../Utilities/components/Buttons';
import {
  fetchStaffPaymentsByUser,
  searchStaffPayments,
  refundPayment,
  resendReceipt,
  voidPayment,
  printReceipt
} from '../../../../../../Utilities/api';
import './ReceptionPayments.css';

const ITEMS_PER_PAGE = 10;

function safeString(v) {
  if (v === null || v === undefined) return '';
  return String(v);
}
function toLower(v) {
  return safeString(v).toLowerCase();
}

export default function ReceptionPayments() {
  const navigate = useNavigate();
  const params = useParams();
  const userId = params.userId;

  // --- server data ---
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);

  // --- ui state ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // typing vs submitted search
  const [searchInput, setSearchInput] = useState('');   // what the user types
  const [submittedQuery, setSubmittedQuery] = useState(''); // what we actually query with

  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [onlyLast90, setOnlyLast90] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);

  // server-side pagination params
  const limit = ITEMS_PER_PAGE;
  const offset = (currentPage - 1) * ITEMS_PER_PAGE;
  const totalPages = Math.max(1, Math.ceil((total || 0) / ITEMS_PER_PAGE));

  // precompute dates (if you show them anywhere)
  const now = useMemo(function () { return new Date(); }, []);
  const ninetyDaysAgo = useMemo(function () {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d;
  }, []);

  // ---- fetcher ----
  async function fetchPage() {
    setLoading(true);
    setError('');
    try {
      var opts = {
        status: statusFilter,
        method: methodFilter,
        from: dateFrom,
        to: dateTo,
        last90: onlyLast90 ? '1' : undefined,
        limit: limit,
        offset: offset
      };

      var result;
      if (submittedQuery && submittedQuery.trim() !== '') {
        // global search: user name/email/phone/referral or pet name
        result = await searchStaffPayments(submittedQuery.trim(), opts);
      } else {
        // scoped to the user on the route
        result = await fetchStaffPaymentsByUser(userId, opts);
      }

      var rows = result && result.rows ? result.rows : [];
      var tot = result && typeof result.total === 'number' ? result.total : rows.length;
      setPayments(rows);
      setTotal(tot);
    } catch (e) {
      var msg = e && e.message ? e.message : 'Failed to load payments';
      setPayments([]);
      setTotal(0);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  // run when filters/pagination/submittedQuery/userId change
  useEffect(function () {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, statusFilter, methodFilter, dateFrom, dateTo, onlyLast90, currentPage, submittedQuery]);

  // keep page valid when total changes
  useEffect(function () {
    var newTotalPages = Math.max(1, Math.ceil((total || 0) / ITEMS_PER_PAGE));
    if (currentPage > newTotalPages) setCurrentPage(1);
  }, [total, currentPage]);

  // ---- search handlers (submit on Enter or button) ----
  function onChangeSearchInput(e) {
    setSearchInput(e.target.value);
  }

  function submitSearch() {
    // apply the typed value to the query and reset to page 1
    setSubmittedQuery(searchInput);
    setCurrentPage(1);
  }

  function onSearchKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitSearch();
    }
  }

  function clearSearch() {
    setSearchInput('');
    setSubmittedQuery('');
    setCurrentPage(1);
  }

  function onClearDates() {
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  }

  // ---- navigation ----
  function openDetails(bk) {
    // redirect to the userPaymentDetails page you specified
    var uid = bk && bk.userId ? bk.userId : userId;
    var id = bk && bk.id ? bk.id : '';
    navigate('/console/userPaymentDetails/' + uid + '/' + id, { state: { transaction: bk } });
  }

  // ---- actions ----
  async function onRefund(p) {
    var ok = window.confirm('Refund payment #' + safeString(p && p.id) + '?');
    if (!ok) return;
    try {
      await refundPayment(p.id);
      fetchPage();
    } catch (e) {
      var msg = e && e.message ? e.message : 'Refund failed';
      alert(msg);
    }
  }

  async function onResend(p) {
    try {
      await resendReceipt(p.id);
      alert('Receipt resent.');
    } catch (e) {
      var msg = e && e.message ? e.message : 'Failed to resend receipt';
      alert(msg);
    }
  }

  async function onVoid(p) {
    var ok = window.confirm('Void authorization for #' + safeString(p && p.id) + '?');
    if (!ok) return;
    try {
      await voidPayment(p.id);
      fetchPage();
    } catch (e) {
      var msg = e && e.message ? e.message : 'Void failed';
      alert(msg);
    }
  }

  async function onPrint(p) {
    try {
      await printReceipt(p.id);
    } catch (e) {
      var msg = e && e.message ? e.message : 'Failed to print';
      alert(msg);
    }
  }

  // ---- render ----
  if (loading) {
    return <div className="spx-loading">Loading staff payments…</div>;
  }
  if (error) {
    return <div className="spx-error">Error: {error}</div>;
  }

  return (
    <div className="spx-wrap">
      <div className="spx-header">
        <BackButton />
        <h2 className="spx-header-title">Staff Payments · User #{userId}</h2>
      </div>

      <div className="spx-toolbar" role="search">
        <div className="spx-searchgroup">
          <input
            className="spx-input"
            placeholder="Search user/pet/email/phone/referral · press Enter or Search"
            value={searchInput}
            onChange={onChangeSearchInput}
            onKeyDown={onSearchKeyDown}
            aria-label="Search payments"
          />
          <button className="spx-btn primary" onClick={submitSearch}>Search</button>
          {submittedQuery || searchInput ? (
            <button className="spx-btn ghost" onClick={clearSearch} aria-label="Clear search">
              Clear
            </button>
          ) : null}
        </div>

        <div className="spx-filters">
          <label className="spx-filter">
            <span>Status</span>
            <select
              className="spx-select"
              value={statusFilter}
              onChange={function (e) { setStatusFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">All</option>
              <option value="succeeded">Succeeded</option>
              <option value="pending">Pending</option>
              <option value="refunded">Refunded</option>
              <option value="voided">Voided</option>
              <option value="failed">Failed</option>
              <option value="canceled">Canceled</option>
            </select>
          </label>

          <label className="spx-filter">
            <span>Method</span>
            <select
              className="spx-select"
              value={methodFilter}
              onChange={function (e) { setMethodFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">All</option>
              <option value="card">Card</option>
              <option value="cash">Cash</option>
              <option value="apple_pay">Apple Pay</option>
              <option value="google_pay">Google Pay</option>
              <option value="ach">ACH</option>
              <option value="other">Other</option>
            </select>
          </label>

          <label className="spx-filter">
            <span>From</span>
            <input
              type="date"
              className="spx-date"
              value={dateFrom}
              onChange={function (e) { setDateFrom(e.target.value); setCurrentPage(1); }}
            />
          </label>
          <label className="spx-filter">
            <span>To</span>
            <input
              type="date"
              className="spx-date"
              value={dateTo}
              onChange={function (e) { setDateTo(e.target.value); setCurrentPage(1); }}
            />
          </label>

          <button className="spx-btn" onClick={onClearDates}>Clear Dates</button>

          <label className="spx-checkbox">
            <input
              type="checkbox"
              checked={onlyLast90}
              onChange={function (e) { setOnlyLast90(e.target.checked); setCurrentPage(1); }}
            />
            <span>Last 90 days</span>
          </label>
        </div>
      </div>

      <div className="spx-summary">
        <div><strong>{total}</strong> results</div>
        <div>
          Page Gross:{' '}
          <strong>
            ${payments.reduce(function (s, p) { return s + Number(p && p.amount ? p.amount : 0); }, 0).toFixed(2)}
          </strong>
        </div>
        <div>
          Page Tips:{' '}
          <strong>
            ${payments.reduce(function (s, p) { return s + Number(p && p.tip ? p.tip : 0); }, 0).toFixed(2)}
          </strong>
        </div>
      </div>

      {payments.length === 0 ? (
        <div className="spx-empty">No payments match your filters.</div>
      ) : (
        <div className="spx-list">
          {payments.map(function (p) {
            var ts = (p && p.paidAt) || (p && p.createdAt) || (p && p.updatedAt) || (p && p.startTime);
            var dt = 'N/A';
            if (ts) {
              var d = new Date(ts);
              dt = isNaN(d.getTime()) ? 'N/A' : d.toLocaleString();
            }

            var pets = [];
            if (p && Array.isArray(p.pets)) pets = p.pets;
            else if (p && Array.isArray(p.selectedPets)) pets = p.selectedPets;

            var petNames = '—';
            if (pets && pets.length > 0) {
              var arr = [];
              for (var i = 0; i < pets.length; i++) {
                var raw = pets[i];
                var nm = '';
                if (raw && typeof raw === 'object' && raw.petName) nm = raw.petName;
                else nm = safeString(raw);
                if (nm) arr.push(nm);
              }
              petNames = arr.join(', ');
            }

            var statusClass = p && p.status ? p.status : 'unknown';
            var amountNum = Number(p && p.amount ? p.amount : 0);
            var tipNum = Number(p && p.tip ? p.tip : 0);
            var refStr = safeString((p && p.processorRef) || (p && p.reference));

            return (
              <div key={p.id} className="spx-row">
                <button
                  type="button"
                  className="spx-col spx-col-main"
                  onClick={function () { openDetails(p); }}
                >
                  <div className="spx-line1">
                    <span className="spx-id">#{safeString(p && p.id)}</span>
                    <span className={'spx-badge ' + toLower(statusClass)}>{toLower(statusClass)}</span>
                    <span className="spx-method">{safeString(p && p.method).toUpperCase() || '—'}</span>
                    <span className="spx-amount">${amountNum.toFixed(2)}</span>
                  </div>
                  <div className="spx-line2">
                    <span className="spx-dt">{dt}</span>
                    <span className="spx-pets">Pets: {petNames}</span>
                    <span className="spx-ref">Ref: {refStr || '—'}</span>
                    {tipNum ? <span className="spx-tip">Tip: ${tipNum.toFixed(2)}</span> : null}
                  </div>
                </button>

                <div className="spx-col spx-actions">
                  {p && p.status === 'succeeded' ? (
                    <>
                      <button className="spx-btn ghost" onClick={function () { onResend(p); }}>Resend</button>
                      <button className="spx-btn" onClick={function () { onPrint(p); }}>Print</button>
                      <button className="spx-btn warn" onClick={function () { onRefund(p); }}>Refund</button>
                    </>
                  ) : null}
                  {p && p.status === 'pending' ? (
                    <button className="spx-btn warn" onClick={function () { onVoid(p); }}>Void</button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="spx-pagination">
        <button
          className="spx-btn"
          disabled={currentPage === 1}
          onClick={function () { setCurrentPage(currentPage - 1); }}
        >
          Previous
        </button>
        <span className="spx-pageinfo">Page {currentPage} of {totalPages}</span>
        <button
          className="spx-btn"
          disabled={currentPage === totalPages}
          onClick={function () { setCurrentPage(currentPage + 1); }}
        >
          Next
        </button>
      </div>
    </div>
  );
}
